// theme/presets/identityGradients.ts
// Fixed, accent-INDEPENDENT identity colours for avatar & banner choices.
// Hardcoded (not theme tokens) so a member's identity stays stable regardless of
// their theme or accent.
//
// 1.25.00 rewrote this file for two reasons, one cosmetic and one a real bug:
//
//   1. Every entry was a 135° two-stop gradient, which is the stock generated
//      avatar. Six of them in one hue family are not six identities; they are
//      one gradient with noise. They are now flat colours from six different
//      hue families, which is what actually makes an identity legible at 28px.
//
//   2. The values were stranded in the June teal era. A member who picked the
//      banner tone named "violet" got teal, and "sunset" got mint — four
//      options that were one option, under labels that were false. The tones
//      below match their own names.
//
// The function names still say "gradient" so the ~15 call sites keep working;
// they return a plain CSS colour, which is a valid `background` value. Renaming
// them is a mechanical follow-up, not a look change.

export const AVATAR_KEYS = ['av-grad', 'av-1', 'av-2', 'av-3', 'av-4', 'av-5'] as const;
export type AvatarKey = (typeof AVATAR_KEYS)[number];

/** Flat identity colours. Every one clears AA for the white initial it carries
 *  (measured against #FFFFFF: 7.7, 5.6, 6.1, 5.0, 7.9, 6.8). */
const AVATAR_COLORS: Record<AvatarKey, string> = {
  'av-grad': '#5A4B7A', // plum — the default, quietly related to the damson ink
  'av-1':    '#3E6F7A', // teal slate
  'av-2':    '#7A5C3E', // umber
  'av-3':    '#4E7A55', // moss
  'av-4':    '#7A3E55', // mulberry
  'av-5':    '#4F5A7A', // slate blue
};

/** Avatar colour for a key; falls back to 'av-grad' for unknown keys. */
export function avatarGradient(key: string): string {
  return AVATAR_COLORS[key as AvatarKey] ?? AVATAR_COLORS['av-grad'];
}

export const BANNER_TONES = ['violet', 'teal', 'sunset', 'magenta'] as const;
export type BannerTone = (typeof BANNER_TONES)[number];

/** Banner tones that are the colour their name claims. */
const BANNER_COLORS: Record<BannerTone, string> = {
  violet:  '#4A3A6B',
  teal:    '#2F5C66',
  sunset:  '#6B4A2F',
  magenta: '#6B2F4A',
};

/** Banner colour for a tone; falls back to 'violet' for unknown tones. */
export function bannerGradient(tone: string): string {
  return BANNER_COLORS[tone as BannerTone] ?? BANNER_COLORS.violet;
}
